import { useQuery, useReactiveVar } from '@apollo/client';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, type Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { Link } from '@tanstack/react-router';
import type React from 'react';
import { useMemo, useState } from 'react';
import { parseSuperView } from 'src/api/viewModels/data';
import HighlightedFragment from 'src/components/HighlightedText/HighlightedFragment';
import HighlightedText from 'src/components/HighlightedText/HighlightedText';
import MatchContext from 'src/components/MatchContext/MatchContext';
import { buildSuperViewEditQuery } from 'src/dynamoQueries/superView';
import { getOwner } from 'src/helpers';
import { formatDateDisplay } from 'src/helpers/dateFields';
import { getDensityConfig } from 'src/helpers/densityConfig';
import type { InitialNavParams } from 'src/helpers/filters';
import { akomiMediaCardGridVar } from 'src/store/akomiMediaCardGridVar';
import viewStateVar from 'src/store/viewState';
import type { CollectionItem, CollectionOwner, SuperViewField } from '../types';

interface GalleryCardMetadataProps {
  collection: CollectionItem;
  /** TanStack Router-compatible navigation params with separate path and search */
  galleryNavParams: InitialNavParams;
  searchHighlights?: string[];
  highlights?: Record<string, string[]>;
  highlightLabels?: Record<string, string>;
  maxFields?: number;
}

interface SuperViewResponse {
  superViewEdit?: SuperViewField[];
}

/**
 * Format field value based on field slug/type
 * Handles special cases like owner, dates, etc.
 * Works with superView data structure (slug, show, dataType)
 */
const formatFieldValue = (field: SuperViewField, collection: CollectionItem): string => {
  const slug = field.slug;
  const dataType = field.dataType;
  const value = field.show;

  // Handle owner field - use collection.owner object
  if (slug === 'owner' && collection?.owner) {
    return getOwner(collection.owner as CollectionOwner) || '';
  }

  // Handle date fields - format with string slicing (no timezone math)
  if (dataType === 'DTIME' || slug === 'date_added') {
    const dateValue = value || collection?.dateAdded;
    return dateValue ? formatDateDisplay(dateValue) : '';
  }

  // Handle name - use collection.name if value is empty
  if (slug === 'name' && !value && collection?.name) {
    return collection.name;
  }

  // Handle media_group - use collection.mediaGroup.name if value is empty
  if (slug === 'media_group' && !value && collection?.mediaGroup?.name) {
    return collection.mediaGroup.name;
  }

  return value || '';
};

/**
 * GalleryCardMetadata - Displays metadata fields on gallery cards
 *
 * Structure (matching Edit Dialog pattern):
 * 1. Gallery Name - Always first (hardcoded, from collection.name)
 * 2. Media Group - Always second (hardcoded, from collection.mediaGroup.name)
 * 3. View-configured fields - From Gallery/GalleryCardMeta view, in admin-defined order
 */
const GalleryCardMetadata: React.FC<GalleryCardMetadataProps> = ({
  collection,
  galleryNavParams,
  searchHighlights = [],
  highlights,
  highlightLabels,
  maxFields: maxFieldsProp,
}) => {
  const { density } = useReactiveVar(akomiMediaCardGridVar);
  const densityConfig = getDensityConfig(density);
  const maxFields = maxFieldsProp ?? densityConfig.maxVisibleFields ?? 4;
  const classes = useStyles({ densityConfig });
  const viewState = useReactiveVar(viewStateVar);
  const viewModels = viewState.viewModels;
  const ViewGalleryCardMeta = viewModels?.gallery?.ViewGalleryCardMeta;
  const [expanded, setExpanded] = useState(false);

  // Use superView query - same as GalleryEditDialog, but with GalleryCardMeta view
  const { data: superViewResponse, loading } = useQuery<SuperViewResponse>(buildSuperViewEditQuery(), {
    skip: !collection?.id || !ViewGalleryCardMeta?.node?.componentName,
    variables: {
      globalId: collection?.id,
      viewComponentName: ViewGalleryCardMeta?.node?.componentName,
    },
    fetchPolicy: 'network-only',
  });

  // Parse superView data - all fields come from the view config (including name and media_group)
  const viewFields = useMemo((): SuperViewField[] => {
    return (
      (parseSuperView(
        superViewResponse as unknown as Parameters<typeof parseSuperView>[0],
      ) as unknown as SuperViewField[]) || []
    );
  }, [superViewResponse]);

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={16} color="secondary" />
      </div>
    );
  }

  // Calculate which metadata fields to show (respecting maxFields)
  const metadataFields = expanded ? viewFields : viewFields.slice(0, maxFields);
  const hiddenCount = viewFields.length - maxFields;
  const hasMore = hiddenCount > 0 && !expanded;

  const handleToggleExpand = (e: React.MouseEvent<HTMLSpanElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setExpanded(!expanded);
  };

  // Render a field - name gets link treatment for navigation, others render normally
  const renderField = (field: SuperViewField, index: number): React.ReactElement => {
    const value = formatFieldValue(field, collection);
    const fallbackIcon = field.fallbackMessage ? (
      <Tooltip title={field.fallbackMessage} arrow>
        <WarningAmberIcon sx={{ fontSize: 14, color: 'text.disabled', ml: 0.5, verticalAlign: 'middle' }} />
      </Tooltip>
    ) : null;

    // Check if OpenSearch highlighted this specific field
    const osHighlight = field.slug && highlights?.[field.slug]?.[0];

    return (
      <div key={index} className={classes.fieldRow}>
        <Typography className={classes.fieldLabel}>{field.label}</Typography>
        <span className={classes.fieldSep}>&#8226;</span>
        <Typography component="span" className={value.trim() ? classes.fieldValue : classes.emptyValue}>
          {value.trim() ? (
            osHighlight ? (
              <HighlightedFragment fragment={osHighlight} />
            ) : (
              <HighlightedText highlightTexts={searchHighlights} text={value} />
            )
          ) : (
            '···'
          )}
        </Typography>
        {fallbackIcon && <span className={classes.fallbackIcon}>{fallbackIcon}</span>}
      </div>
    );
  };

  return (
    <div className={classes.metadataContainer}>
      {/* Title — always collection name, matching playlist fallback */}
      <Typography component="div" className={classes.titleField}>
        <Link className={classes.titleLink} to={galleryNavParams.to} search={galleryNavParams.search}>
          <HighlightedText highlightTexts={searchHighlights} text={collection?.name || 'Untitled'} />
        </Link>
      </Typography>
      {/* Metadata fields from superView in configured order */}
      {metadataFields.map((field, index) => renderField(field, index))}

      {hasMore && (
        <Typography className={classes.moreIndicator} onClick={handleToggleExpand}>
          +{hiddenCount} more...
        </Typography>
      )}
      {expanded && viewFields.length > maxFields && (
        <Typography className={classes.moreIndicator} onClick={handleToggleExpand}>
          Show less
        </Typography>
      )}
      {highlights && Object.keys(highlights).length > 0 && (
        <MatchContext highlights={highlights} slugLabels={highlightLabels} />
      )}
    </div>
  );
};

export default GalleryCardMetadata;

interface StyleProps {
  densityConfig: import('src/helpers/densityConfig').DensityConfig;
}

const useStyles = makeStyles((theme: Theme) => ({
  metadataContainer: {
    // Match asset card layout
  },
  titleField: (props: StyleProps) => ({
    // Match asset card title styling
    marginBottom: theme.spacing(props.densityConfig.titleMarginBottom),
    display: 'block',
  }),
  titleLink: (props: StyleProps) => ({
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: props.densityConfig.titleFontSize,
    color: theme.palette.text.primary,
    textDecoration: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  }),
  fieldRow: (props: StyleProps) => ({
    display: 'flex',
    alignItems: 'baseline',
    gap: '0px',
    padding: props.densityConfig.fieldRowPadding,
    margin: props.densityConfig.fieldRowMargin,
    overflow: 'hidden',
  }),
  fieldLabel: (props: StyleProps) => ({
    color: theme.palette.text.disabled,
    fontSize: props.densityConfig.fieldLabelFontSize,
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  }),
  fieldSep: {
    color: theme.palette.text.disabled,
    flexShrink: 0,
    fontSize: '0.5rem',
    margin: '0 5px',
    lineHeight: 1,
  },
  fieldValue: (props: StyleProps) => ({
    flex: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: props.densityConfig.fieldValueFontSize,
  }),
  emptyValue: (props: StyleProps) => ({
    color: alpha(theme.palette.text.primary, 0.12),
    fontSize: props.densityConfig.fieldValueFontSize,
    letterSpacing: 2,
  }),
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1),
  },
  moreIndicator: {
    color: theme.palette.text.disabled,
    fontSize: '0.85rem',
    fontStyle: 'italic',
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  fallbackIcon: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
  },
}));
